import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Coins, Zap, TrendingUp, Calendar, CreditCard, Sparkles, Crown, Star, MessageCircle, Bot } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { toast } from 'sonner';
import { AppHeader } from '@/components/AppHeader';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';

const CREDIT_PACKS = [
  {
    name: '[TEST] 10 Credits',
    credits: 10,
    price: '£0.01',
    priceId: 'price_1ScmNHDADiSUiGodMcAjpMRL',
    description: 'Test purchase - 1 penny',
    perCredit: '£0.001',
    icon: Coins,
    isTest: true,
  },
  {
    name: '100 Credits',
    credits: 100,
    price: '£4.99',
    priceId: 'price_credit_100',
    description: 'Perfect for occasional use',
    perCredit: '£0.0499',
    icon: Coins,
  },
  {
    name: '250 Credits',
    credits: 250,
    price: '£9.99',
    priceId: 'price_credit_250',
    description: 'Great value for regular users',
    perCredit: '£0.0400',
    savings: '20%',
    icon: Zap,
    popular: true,
  },
  {
    name: '500 Credits',
    credits: 500,
    price: '£17.99',
    priceId: 'price_credit_500',
    description: 'Best for power users',
    perCredit: '£0.0360',
    savings: '28%',
    icon: TrendingUp,
  },
  {
    name: '1000 Credits',
    credits: 1000,
    price: '£29.99',
    priceId: 'price_credit_1000',
    description: 'Maximum value',
    perCredit: '£0.0300',
    savings: '40%',
    icon: Sparkles,
  },
];

const AI_FEATURES = [
  { name: 'AI Therappi®', icon: Bot, description: 'QuantumSelf Psychology™ therapy' },
  { name: 'Dream Analysis', icon: MessageCircle, description: 'Symbolic dream interpretation' },
  { name: 'SoulBot Guidance', icon: Sparkles, description: 'Intuitive message interpretation' },
];

export default function Credits() {
  const [user, setUser] = useState<User | null>(null);
  const navigate = useNavigate();
  useNavigationHistory(); // Track navigation
  
  const {
    monthlyCredits,
    purchasedCredits,
    totalCredits,
    creditsResetDate,
    checkCredits,
    purchaseCredits,
    isSubscribed,
    hasAITherappiAccess,
    createCheckout,
    isTrialActive,
  } = useSubscription();

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  useEffect(() => {
    checkCredits();
  }, []);

  const handlePurchaseCredits = async (priceId: string) => {
    if (isTrialActive) {
      toast.error("Can't purchase during trial", {
        description: "Subscribe to AI Therappi® first, then you can purchase credit packs anytime.",
        action: {
          label: "Subscribe",
          onClick: () => navigate('/subscription')
        },
        duration: 5000
      });
      return;
    }
    
    try {
      const url = await purchaseCredits(priceId);
      window.open(url, '_blank');
      toast.success('Redirecting to checkout...');
    } catch (error) {
      console.error('Error purchasing credits:', error);
      toast.error('Failed to start checkout');
    }
  };

  const handleUpgradeSubscription = async () => {
    try {
      // Redirect to subscription page
      navigate('/subscription');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to navigate');
    }
  };

  const creditsPercentage = monthlyCredits > 0 ? (monthlyCredits / 300) * 100 : 0;
  const resetDate = creditsResetDate ? new Date(creditsResetDate).toLocaleDateString() : 'N/A';

  return (
    <div className="min-h-screen bg-background">
      <AppHeader user={user} onSignOut={handleSignOut} />

      <div className="container mx-auto py-12 px-6 max-w-7xl">
        {/* Current Credits Overview */}
        <div className="grid gap-6 md:grid-cols-3 mb-12">
          <Card className="border-2 border-primary/20">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Coins className="h-4 w-4" />
                Total Credits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-primary">{totalCredits}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Available for AI conversations
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Monthly Credits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{monthlyCredits}</div>
              <div className="mt-3 space-y-2">
                <Progress value={creditsPercentage} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  Resets on {resetDate}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Purchased Credits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold text-green-500">{purchasedCredits}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Never expire • Roll over monthly
              </p>
            </CardContent>
          </Card>
        </div>

        {/* AI Features Using Credits */}
        <Card className="mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              AI Features Using Credits
            </CardTitle>
            <CardDescription>
              1 credit = 1 complete conversation exchange (your message + AI response)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              {AI_FEATURES.map((feature) => (
                <div
                  key={feature.name}
                  className="flex items-start gap-3 p-4 rounded-lg border bg-card"
                >
                  <feature.icon className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm">{feature.name}</h3>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Trial Users Notice */}
        {isTrialActive && (
          <Card className="mb-12 border-2 border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-950/20 dark:to-yellow-950/20">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                Free Trial Active
              </CardTitle>
              <CardDescription>
                You can't purchase credit packs during your trial, but you can subscribe anytime
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-background/50 rounded-lg space-y-2">
                <p className="text-sm font-medium">During your trial:</p>
                <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                  <li>Full access to all features with your trial credits</li>
                  <li>Subscribe to AI Therappi® anytime to get 300 monthly credits</li>
                  <li>After subscribing, you can purchase credit packs as needed</li>
                </ul>
              </div>
              <Button onClick={handleUpgradeSubscription} className="w-full gap-2">
                <Star className="h-4 w-4" />
                Subscribe Now
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Subscription Status & Actions */}
        {!hasAITherappiAccess() && !isTrialActive && (
          <Card className="mb-12 border-2 border-primary/30 bg-primary/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="h-5 w-5 text-primary" />
                Get Monthly Credits with AI Therappi®
              </CardTitle>
              <CardDescription>
                Subscribe to get 300 AI conversation credits every month
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <div className="space-y-1">
                  <p className="font-semibold">AI Therappi® Subscription</p>
                  <p className="text-sm text-muted-foreground">
                    From £5.99/month • 300 credits monthly • QuantumSelf Psychology™
                  </p>
                </div>
                <Button onClick={handleUpgradeSubscription} className="gap-2">
                  <Star className="h-4 w-4" />
                  View Plans
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Purchase Credit Packs */}
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">Purchase Credit Packs</h2>
            <p className="text-muted-foreground">
              Credits never expire and work across all AI features
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {CREDIT_PACKS.map((pack) => {
              const Icon = pack.icon;
              return (
                <Card
                  key={pack.priceId}
                  className={`relative ${
                    pack.popular ? 'border-2 border-primary shadow-lg' : ''
                  }`}
                >
                  {pack.popular && (
                    <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-2">
                      <Icon className="h-8 w-8 text-primary" />
                      {pack.savings && (
                        <Badge variant="secondary" className="text-xs">
                          Save {pack.savings}
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl">{pack.name}</CardTitle>
                    <CardDescription>{pack.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="text-3xl font-bold">{pack.price}</div>
                      <div className="text-sm text-muted-foreground">
                        {pack.perCredit} per credit
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Coins className="h-4 w-4" />
                        {pack.credits} conversations
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Sparkles className="h-4 w-4" />
                        Never expires
                      </div>
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Bot className="h-4 w-4" />
                        All AI features
                      </div>
                    </div>
                    <Button
                      onClick={() => handlePurchaseCredits(pack.priceId)}
                      className="w-full"
                      variant={pack.popular ? 'default' : 'outline'}
                      disabled={isTrialActive}
                    >
                      {isTrialActive ? 'Subscribe First' : 'Purchase'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ */}
        <Card className="mt-12">
          <CardHeader>
            <CardTitle>How Credits Work</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div>
              <h4 className="font-semibold mb-1">Credit Usage by AI Feature</h4>
              <p className="text-muted-foreground mb-2">
                <strong className="text-primary">Therappi<sup className="text-[0.6em]">®</sup> AI (Therapeutic Chat):</strong> 3 credits per conversation exchange
              </p>
              <p className="text-muted-foreground mb-2">
                Advanced therapeutic dialogue with deep reasoning and emotional intelligence.
              </p>
              <p className="text-muted-foreground mb-2">
                <strong className="text-primary">Dream Analysis & SoulBot:</strong> 1 credit per conversation exchange
              </p>
              <p className="text-muted-foreground">
                Supportive interactions for dream interpretation and spiritual guidance.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Monthly vs. Purchased Credits</h4>
              <p className="text-muted-foreground">
                <strong>Monthly credits</strong> (300) come with AI Therappi® subscriptions and reset every 30 days. <strong>Purchased credits</strong> never expire and are used after monthly credits are depleted.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">Which AI features use credits?</h4>
              <p className="text-muted-foreground">
                Credits work across AI Therappi®, Dream Analysis, and SoulBot Guidance. One credit pool for all your AI conversations.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-1">What happens when I run out?</h4>
              <p className="text-muted-foreground">
                When your credits reach zero, you'll need to purchase a credit pack or subscribe to AI Therappi® to continue conversations.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
